import type { AnyError } from "@pdfking/errors"
import { AutoThrow } from "@pdfking/errors"

import { SafePromise } from "./SafePromise"

/**
 * Executes a promise with guaranteed cleanup callback execution.
 * The cleanup function is called whether the promise succeeds or fails.
 *
 * @param input - The promise to execute
 * @param close - Cleanup function called with error (if any) after promise settles
 * @returns A promise that resolves to the original promise's value
 * @throws The original error if the promise rejects
 *
 * @example
 * ```ts
 * import { WithCleanup } from "@pdfking/tfunc"
 *
 * // Success: cleanup runs, then the value comes through
 * await WithCleanup(Promise.resolve(42), error => console.log("cleanup, failed:", Boolean(error)))
 * // cleanup, failed: false
 * // 42
 *
 * // Failure: cleanup runs with the error, then the error is rethrown
 * await WithCleanup(Promise.reject(new Error("Failed")), error =>
 *   console.log("cleanup, failed:", Boolean(error))
 * ).catch(() => "caught")
 * // cleanup, failed: true
 * // "caught"
 * ```
 */
export const WithCleanup = <T>(
  input: Promise<T>,
  close: (error: AnyError | undefined) => void | Promise<void>
): Promise<T> =>
  SafePromise(input).then(async result => {
    await close(result.error)
    return AutoThrow(result)
  })
